// v2 lion — a proper maned male: a long tawny body with deep haunches and a
// pale belly, the signature three-layer mane (a dark outer ruff, a russet mid
// ruff and a hanging chest bib) framing a lighter face with a pale muzzle, round
// dark-backed ears peeking from the ruff, and a long tail ending in the dark
// tuft. Two coats: golden with an umber mane and a paler sandy form with a
// russet mane. Ported 1:1 from age-of-mcp's v2/lion.ts, using the library's
// shared `critter` (quadLegs) and `safari` (eyes, tube) helpers.
import * as THREE from "three";
import { defineModel, critter, safari, type Part } from "../../lib";

const COATS = [
  { coat: "#c79550", haunch: "#bd8b48", belly: "#dbb87e", maneOut: "#6e4520",
    maneMid: "#8a5a2c", face: "#d8ad66", muzzle: "#ecdfc2", paw: "#b8884a" },
  { coat: "#d2ab6a", haunch: "#c8a05e", belly: "#e6cb96", maneOut: "#8a5e32",
    maneMid: "#a3743e", face: "#e2bd7e", muzzle: "#f0e6cc", paw: "#c49a5c" },
];

function lionParts(v: number): Part[] {
  const c = COATS[v % COATS.length]!;
  const parts: Part[] = critter.quadLegs({
    dx: 0.062, dz: 0.032, bodyY: 0.1, rTop: 0.011, rFoot: 0.009,
    color: c.coat, hoof: { color: c.paw, r: 0.0115, h: 0.012 },
  });
  parts.push(
    // long tawny body, deep haunches, pale belly
    { geometry: new THREE.IcosahedronGeometry(0.058, 0), color: c.coat,
      position: [-0.005, 0.1, 0], scale: [1.75, 0.95, 0.88] },
    { geometry: new THREE.IcosahedronGeometry(0.042, 0), color: c.haunch,
      position: [-0.08, 0.1, 0], scale: [1.05, 1.0, 0.95] },
    { geometry: new THREE.IcosahedronGeometry(0.038, 0), color: c.belly,
      position: [-0.01, 0.085, 0], scale: [1.4, 0.7, 0.82] },
    // the layered mane: dark outer ruff, russet mid ruff, hanging chest bib
    { geometry: new THREE.IcosahedronGeometry(0.062, 0), color: c.maneOut,
      position: [0.092, 0.115, 0], scale: [0.9, 1.1, 1.05] },
    { geometry: new THREE.IcosahedronGeometry(0.05, 0), color: c.maneMid,
      position: [0.105, 0.118, 0], scale: [0.85, 1.05, 1.0] },
    { geometry: new THREE.IcosahedronGeometry(0.035, 0), color: c.maneOut,
      position: [0.085, 0.075, 0], scale: [0.8, 1.1, 0.9] },
    // lighter face with a pale muzzle and dark nose
    { geometry: new THREE.IcosahedronGeometry(0.03, 0), color: c.face,
      position: [0.135, 0.122, 0] },
    { geometry: new THREE.IcosahedronGeometry(0.016, 0), color: c.muzzle,
      position: [0.158, 0.112, 0], scale: [1.1, 0.8, 1.0] },
    { geometry: new THREE.IcosahedronGeometry(0.006, 0), color: "#2c1e14",
      position: [0.172, 0.117, 0] },
    // round dark-backed ears peeking out of the ruff
    { geometry: new THREE.IcosahedronGeometry(0.0095, 0), color: c.maneMid,
      position: [0.118, 0.155, 0.024] },
    { geometry: new THREE.IcosahedronGeometry(0.0095, 0), color: c.maneMid,
      position: [0.118, 0.155, -0.024] },
    // long tail with the dark tuft
    safari.tube([-0.115, 0.105, 0], [-0.15, 0.05, 0.01], 0.005, 0.003, c.coat),
    { geometry: new THREE.IcosahedronGeometry(0.009, 0), color: c.maneOut,
      position: [-0.155, 0.045, 0.012] },
  );
  parts.push(...safari.eyes(0.148, 0.13, 0.013, 0.004, "#231a10"));
  return parts;
}

export const lionV2 = defineModel({
  id: "lion-v2",
  scale: 2.3, // real-world size correction (see Model.scale)
  batchId: "fable",
  name: "Lion (v2)",
  tags: ["creature","lion","low-poly","age-of-mcp","fable"],
  summary: "Low-poly lion.",
  description: "Ported from the age-of-mcp gallery, v2 style.",
  author: "age-of-mcp",
  footprint: 0.15,
  variants: [
    { id: "variant-0", label: "Variant 1", parts: () => lionParts(0) },
    { id: "variant-1", label: "Variant 2", parts: () => lionParts(1) },
  ],
  animations: [],
});
